/**
 * AudioSource Component
 *
 * Plays audio clips with full control over playback, volume, pitch,
 * spatial audio, and bus routing through the Audio Mixer.
 *
 * Supports:
 * - Play on awake
 * - Looping
 * - Volume & pitch control
 * - Bus routing (Master, Music, SFX, UI, Ambient, custom)
 * - Full spatial audio (distance rolloff, cone, doppler)
 * - Priority-based voice stealing
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  allowMultiple,
  requireComponent,
  executeInEditor,
  field,
  customEditor,
} from "../attributes";
import { EngineComponent } from "../scripting/EngineComponent";

@component("core.AudioSource")
@customEditor("AudioSourceEditor")
@displayName("Audio Source")
@icon("Volume2")
@color("#4ade80")
@category("Audio")
@allowMultiple(true)
@requireComponent(["core.Transform"])
@executeInEditor(false)
export class AudioSource extends EngineComponent {
  // =========================================================================
  // CLIP & PLAYBACK
  // =========================================================================

  /** Audio clip asset GUID */
  @field({ type: "asset", assetType: "audio", label: "Audio Clip", tooltip: "Drag an audio file here", default: "" })
  clip: string = "";

  /** Audio mixer asset GUID (required) */
  @field({ type: "asset", assetType: "audiomixer", label: "Mixer", tooltip: "Audio mixer asset", default: "" })
  mixerAsset: string = "";

  /** Target bus ID in the mixer */
  @field({ type: "string", label: "Output Bus", tooltip: "Bus to route audio through (e.g., master, music, sfx)", default: "master" })
  outputBus: string = "master";

  /** Play automatically when the scene starts */
  @field({ type: "bool", label: "Play On Awake", default: true })
  playOnAwake: boolean = true;

  /** Loop the audio clip */
  @field({ type: "bool", label: "Loop", default: false })
  loop: boolean = false;

  // =========================================================================
  // VOLUME & PITCH
  // =========================================================================

  /** Volume (0 = silent, 1 = full) */
  @field({ type: "f32", label: "Volume", min: 0, max: 1, step: 0.01, group: "Volume", default: 1 })
  volume: number = 1;

  /** Pitch multiplier (1 = normal). Affects playback speed. */
  @field({ type: "f32", label: "Pitch", min: 0.01, max: 3, step: 0.01, group: "Volume", default: 1 })
  pitch: number = 1;

  /** Mute the source (pauses output without affecting position) */
  @field({ type: "bool", label: "Mute", group: "Volume", default: false })
  mute: boolean = false;

  /** Priority for voice stealing (0 = highest priority, 256 = lowest) */
  @field({ type: "i32", label: "Priority", min: 0, max: 256, group: "Volume", default: 128 })
  priority: number = 128;

  // =========================================================================
  // SPATIAL AUDIO
  // =========================================================================

  /** Enable 3D spatial audio positioning */
  @field({ type: "bool", label: "Spatial Blend", tooltip: "Enable 3D spatial audio", group: "Spatial", default: false })
  spatialBlend: boolean = false;

  /** Distance model for attenuation */
  @field({ type: "string", label: "Distance Model", tooltip: "linear, inverse, or exponential", group: "Spatial", default: "inverse" })
  distanceModel: string = "inverse";

  /** Panning model */
  @field({ type: "string", label: "Panning Model", tooltip: "HRTF or equalpower", group: "Spatial", default: "HRTF" })
  panningModel: string = "HRTF";

  /** Distance at which volume starts to decrease */
  @field({ type: "f32", label: "Min Distance", min: 0, max: 1000, step: 0.5, group: "Spatial", default: 1 })
  minDistance: number = 1;

  /** Distance at which volume reaches 0 (for linear) or approaches 0 (for inverse/exponential) */
  @field({ type: "f32", label: "Max Distance", min: 0, max: 10000, step: 1, group: "Spatial", default: 100 })
  maxDistance: number = 100;

  /** Rolloff speed (how fast volume decreases with distance) */
  @field({ type: "f32", label: "Rolloff Factor", min: 0, max: 10, step: 0.1, group: "Spatial", default: 1 })
  rolloffFactor: number = 1;

  /** Inner cone angle in degrees (full volume inside) */
  @field({ type: "f32", label: "Inner Cone Angle", min: 0, max: 360, step: 1, group: "Spatial", default: 360 })
  coneInnerAngle: number = 360;

  /** Outer cone angle in degrees */
  @field({ type: "f32", label: "Outer Cone Angle", min: 0, max: 360, step: 1, group: "Spatial", default: 360 })
  coneOuterAngle: number = 360;

  /** Volume outside the outer cone (0..1) */
  @field({ type: "f32", label: "Outer Cone Gain", min: 0, max: 1, step: 0.01, group: "Spatial", default: 0 })
  coneOuterGain: number = 0;

  /** Doppler effect strength for this source */
  @field({ type: "f32", label: "Doppler Level", min: 0, max: 5, step: 0.1, group: "Spatial", default: 1 })
  dopplerLevel: number = 1;

  // =========================================================================
  // FADE
  // =========================================================================

  /** Fade in time when play starts (seconds) */
  @field({ type: "f32", label: "Fade In", min: 0, max: 10, step: 0.1, group: "Fade", default: 0 })
  fadeInTime: number = 0;

  /** Fade out time when stop is called (seconds) */
  @field({ type: "f32", label: "Fade Out", min: 0, max: 10, step: 0.1, group: "Fade", default: 0 })
  fadeOutTime: number = 0;

  // ──────────────────────────────────────────────────────────────────────────
  // Runtime API (Unity-style). Not @field → not serialized. Methods proxy to engine.audio
  // for THIS source entity; the AudioSubsystem owns the voice (entity→GUID→voice), so play
  // creates a tracked voice that stop/pause/resume control. Usage from a script:
  // `this.getComponent(Components.AudioSource)?.play()`.
  // ──────────────────────────────────────────────────────────────────────────

  /** (Re)start this source's configured clip as a tracked voice. */
  play(): void {
    this.engine?.audio?.play?.(this.entity);
  }

  /** Stop this source's voice. */
  stop(): void {
    this.engine?.audio?.stop?.(this.entity);
  }

  /** Pause this source's voice. */
  pause(): void {
    this.engine?.audio?.pause?.(this.entity);
  }

  /** Resume this source's paused voice. */
  resume(): void {
    this.engine?.audio?.resume?.(this.entity);
  }
}
